import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Store, Eye, Loader2, CheckCircle } from 'lucide-react';
import { Pagination } from '../ListControls';
import { STATUS_STYLES, BUSINESS_TYPES } from './constants';

/**
 * Selectable + paginated merchant list. Pure presentational — receives all
 * state + handlers from the parent so the coordinator owns the business logic.
 */
export function MerchantsList({
  merchants, loading, statusFilter,
  selectedIds, bulkApproving,
  toggleSelect, selectAllPending, clearSelection, handleBulkApprove,
  openDetail,
  skip, limit, total, onPageChange, onLimitChange,
}) {
  return (
    <>
      {/* Bulk-action toolbar */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-4 py-2" data-testid="bulk-action-toolbar">
          <span className="text-[#00E5FF] text-sm font-medium" data-testid="bulk-selected-count">{selectedIds.length} selected</span>
          <Button
            size="sm"
            onClick={handleBulkApprove}
            disabled={bulkApproving}
            className="bg-[#00E676] text-[#001627] hover:bg-[#00C853] h-8"
            data-testid="bulk-approve-btn"
          >
            {bulkApproving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
            Bulk Approve
          </Button>
          <button type="button" onClick={clearSelection} className="text-xs text-[#A0B4CC] hover:text-white underline-offset-2 hover:underline" data-testid="bulk-clear-btn">
            Clear selection
          </button>
        </div>
      )}

      {/* Select-all helper */}
      {!loading && merchants.some((m) => m.merchant_status === 'pending') && selectedIds.length === 0 && statusFilter === 'pending' && (
        <div className="flex items-center justify-between text-xs text-[#A0B4CC]">
          <button type="button" onClick={selectAllPending} className="hover:text-white inline-flex items-center gap-1.5" data-testid="bulk-select-all-pending">
            <CheckCircle className="w-3.5 h-3.5" /> Select all pending on this page
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /></div>
      ) : merchants.length === 0 ? (
        <div className="text-center py-20 text-[#5C7599]">
          <Store className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No merchants found</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {merchants.map((m) => {
            const mi = m.merchant_info || {};
            const st = STATUS_STYLES[m.merchant_status] || STATUS_STYLES.pending;
            const isSelectable = m.merchant_status === 'pending' || m.merchant_status === 'rejected';
            const isSelected = selectedIds.includes(m.id);
            return (
              <Card
                key={m.id}
                className={`bg-[#041A3B] border ${isSelected ? 'border-[#00E5FF]/50 ring-1 ring-[#00E5FF]/30' : 'border-white/10'} hover:border-white/20 transition-colors`}
                data-testid={`merchant-card-${m.id}`}
              >
                <CardContent className="p-4 flex items-center gap-3">
                  {isSelectable && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => { e.stopPropagation(); toggleSelect(m.id); }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-4 h-4 accent-[#00E5FF] flex-shrink-0 cursor-pointer"
                      data-testid={`merchant-checkbox-${m.id}`}
                      aria-label={`Select ${mi.business_name || m.name} for bulk action`}
                    />
                  )}
                  <div className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(m)}>
                    {mi.business_logo_url ? (
                      <img
                        src={mi.business_logo_url}
                        alt={mi.business_name || 'Logo'}
                        className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-white/10 bg-[#000B18]"
                        onError={(e) => { e.target.onerror = null; e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                        data-testid={`merchant-logo-${m.id}`}
                      />
                    ) : null}
                    <div className="w-12 h-12 rounded-xl bg-[#00E676]/10 items-center justify-center flex-shrink-0"
                      style={{ display: mi.business_logo_url ? 'none' : 'flex' }}>
                      <Store className="w-6 h-6 text-[#00E676]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold text-sm">{mi.business_name || 'Unnamed Business'}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${st.bg} ${st.text} ${st.border}`}>{st.label}</span>
                        {m.role === 'member' && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-[#00E5FF]/10 text-[#00E5FF] border-[#00E5FF]/20" title="Existing AGA member applied as merchant partner">
                            Member-applicant
                          </span>
                        )}
                      </div>
                      <p className="text-[#A0B4CC] text-xs mt-0.5">{m.name} &middot; {m.email}</p>
                      <p className="text-[#5C7599] text-[10px] mt-0.5">
                        {BUSINESS_TYPES[mi.business_type] || mi.business_type || 'Unspecified'} &middot; Applied {new Date(m.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Eye className="w-4 h-4 text-[#5C7599] flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Pagination
        skip={skip}
        limit={limit}
        total={total}
        onPageChange={onPageChange}
        onLimitChange={onLimitChange}
      />
    </>
  );
}
